import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Filter,
  FileText,
  AlertCircle,
  RefreshCw,
  ArrowDownToLine,
  ArrowUpFromLine,
  Package,
  AlertTriangle,
  Boxes,
  SlidersHorizontal,
} from 'lucide-react';
import SortableTableHeader from '../components/SortableTableHeader';
import DateRangeSelect from '../components/DateRangeSelect';
import SearchableSelect, { type SearchableSelectOption } from '../components/SearchableSelect';
import InventoryMovementTotalsBar from '../components/transactions/InventoryMovementTotalsBar';
import { getInventoryTransactions } from '../utils/inventoryTransactionApi';
import { getItems, getItemById } from '../utils/itemApi';
import { ApiValidationError } from '../types/auth';
import type {
  InventoryTransaction,
  InventoryTransactionSummary,
  InventoryTransactionAction,
} from '../types/inventoryTransaction';
import type { Item } from '../types/item';
import type { PaginationMeta } from '../types/journalEntry';
import { formatCurrency, formatQuantity } from '../utils/formatCurrency';
import {
  getDateRangeForPreset,
  todayIso,
  type DateRangePreset,
} from '../utils/dateRangePresets';
import { daysAgoLocalIso } from '../utils/localDate';
import {
  encodeInventorySort,
  loadInventoryTablePreferences,
  parseInventorySort,
  saveInventoryTablePreferences,
  type InventorySortField,
  type SortDir,
} from '../utils/inventoryTablePreferences';
import { useInventoryTransactionsHub } from './inventory-transactions/InventoryTransactionsHubContext';
import {
  useInventoryTransactionFilters,
  type TypeFilter,
} from './inventory-transactions/useInventoryTransactionFilters';
import {
  computeInventoryMovementTotals,
  inventoryMovementRowKey,
} from '../utils/inventoryTransactionSelection';

type SortKey = InventorySortField;

const TYPE_TABS: { id: TypeFilter; label: string }[] = [
  { id: 'all', label: 'All movements' },
  { id: 'receipt', label: 'Receipts' },
  { id: 'sale', label: 'Sales' },
  { id: 'adjustments', label: 'Adjustments' },
  { id: 'starting_value', label: 'Opening stock' },
  { id: 'oversell', label: 'Oversell & true-up' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

const movementTypeLabel = (type: InventoryTransaction['movement_type']) => {
  switch (type) {
    case 'RECEIPT':
      return 'Receipt';
    case 'SALE':
      return 'Sale';
    case 'ADJUSTMENT_IN':
      return 'Adj. in';
    case 'ADJUSTMENT_OUT':
      return 'Adj. out';
    case 'STARTING_VALUE':
      return 'Opening stock';
    case 'BACKORDER_SALE':
      return 'Backorder sale';
    case 'COGS_TRUE_UP':
      return 'COGS true-up';
    default:
      return formatLabel(type);
  }
};

const movementTypeBadgeClass = (type: InventoryTransaction['movement_type']) => {
  switch (type) {
    case 'RECEIPT':
      return 'bg-blue-100 text-blue-800';
    case 'SALE':
      return 'bg-violet-100 text-violet-800';
    case 'ADJUSTMENT_IN':
      return 'bg-emerald-100 text-emerald-800';
    case 'ADJUSTMENT_OUT':
      return 'bg-orange-100 text-orange-800';
    case 'STARTING_VALUE':
      return 'bg-cyan-100 text-cyan-800';
    case 'BACKORDER_SALE':
      return 'bg-rose-100 text-rose-800';
    case 'COGS_TRUE_UP':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const parseItemFilterId = (value: string) => {
  if (!value) return undefined;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : undefined;
};

/** API defaults to 365 days when dates are omitted; widen for the "All dates" preset. */
const resolveFetchDates = (
  preset: DateRangePreset,
  start: string,
  end: string
): { start_date?: string; end_date?: string } => {
  if (start && end) {
    return { start_date: start, end_date: end };
  }
  if (preset === 'all') {
    return { start_date: daysAgoLocalIso(3650), end_date: todayIso() };
  }
  return {
    start_date: start || undefined,
    end_date: end || undefined,
  };
};

const itemToOption = (item: Item): SearchableSelectOption => ({
  value: item.id,
  label: `${item.sku} — ${item.name}`,
  description: item.item_type ? formatLabel(item.item_type) : undefined,
});

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const formatTime = (dateString: string) =>
  new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const parseNum = (value: string) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const parseQty = (value: string | null | undefined) => {
  if (value == null || value === '') return 0;
  return parseNum(value);
};

const receiptHasBackorderSplit = (row: InventoryTransaction) =>
  row.movement_type === 'RECEIPT' && parseQty(row.qty_applied_to_backorder) > 0;

const shouldShowRowMemo = (row: InventoryTransaction) => {
  if (!row.memo?.trim()) return false;
  if (
    receiptHasBackorderSplit(row) &&
    /applied to backorder|to stock/i.test(row.memo)
  ) {
    return false;
  }
  return true;
};

const renderReceiptQtySplit = (row: InventoryTransaction) => {
  if (!receiptHasBackorderSplit(row)) return null;

  const toStock = parseQty(row.qty_added_to_stock);

  return (
    <div className="text-xs mt-0.5 space-y-0.5 font-normal">
      <div className="text-amber-700">
        {formatQuantity(row.qty_applied_to_backorder!)} to backorder
      </div>
      {toStock > 0 && (
        <div className="text-emerald-700">
          {formatQuantity(row.qty_added_to_stock!)} to stock
        </div>
      )}
    </div>
  );
};

const compareStrings = (a: string | null | undefined, b: string | null | undefined) => {
  const left = a ?? '';
  const right = b ?? '';
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return left.localeCompare(right);
};

const compareMovements = (
  a: InventoryTransaction,
  b: InventoryTransaction,
  key: SortKey,
  dir: SortDir
) => {
  const mul = dir === 'asc' ? 1 : -1;

  switch (key) {
    case 'movement_type':
    case 'reference_number':
      return mul * compareStrings(a[key], b[key]);
    case 'counterparty_name': {
      const aName = a.counterparty_name ?? a.memo ?? '';
      const bName = b.counterparty_name ?? b.memo ?? '';
      return mul * compareStrings(aName, bName);
    }
    case 'item_name':
      return (
        mul *
        (compareStrings(a.item_sku, b.item_sku) ||
          compareStrings(a.item_name, b.item_name))
      );
    case 'qty_change':
    case 'value_change':
      return mul * (parseNum(a[key]) - parseNum(b[key]));
    case 'unit_cost': {
      const aCost = a.unit_cost ?? '';
      const bCost = b.unit_cost ?? '';
      if (!aCost && !bCost) return 0;
      if (!aCost) return 1;
      if (!bCost) return -1;
      return mul * (parseNum(aCost) - parseNum(bCost));
    }
    case 'qty_on_hand_after': {
      const aQty = a.qty_on_hand_after ?? '';
      const bQty = b.qty_on_hand_after ?? '';
      if (!aQty && !bQty) return 0;
      if (!aQty) return 1;
      if (!bQty) return -1;
      return mul * (parseNum(aQty) - parseNum(bQty));
    }
    case 'txn_date':
      return (
        mul *
        (compareStrings(a.txn_date, b.txn_date) ||
          compareStrings(a.created_at, b.created_at))
      );
    default:
      return 0;
  }
};

const defaultSortDir = (key: SortKey): SortDir =>
  key === 'txn_date' ||
  key === 'qty_change' ||
  key === 'value_change' ||
  key === 'qty_on_hand_after'
    ? 'desc'
    : 'asc';

const InventoryTransactions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { registerRefetch } = useInventoryTransactionsHub();
  const { filters, listSearch, updateFilters, clearFilters, hasActiveFilters } =
    useInventoryTransactionFilters();
  const {
    typeFilter,
    itemFilter,
    page,
    datePreset,
    startDate,
    endDate,
    searchQuery,
  } = filters;

  const [movements, setMovements] = useState<InventoryTransaction[]>([]);
  const [summary, setSummary] = useState<InventoryTransactionSummary | null>(null);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [itemOptions, setItemOptions] = useState<SearchableSelectOption[]>([]);
  const [itemSearchInput, setItemSearchInput] = useState('');
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const [selectedItemOption, setSelectedItemOption] = useState<SearchableSelectOption | null>(null);
  const selectedItemOptionRef = useRef<SearchableSelectOption | null>(null);
  const itemLookupRef = useRef<Map<number, Item>>(new Map());
  const fetchRequestIdRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [perPage, setPerPage] = useState<number>(() => loadInventoryTablePreferences().perPage);
  const [sortKey, setSortKey] = useState<SortKey>(() => {
    const parsed = parseInventorySort(loadInventoryTablePreferences().sort);
    return parsed?.field ?? 'txn_date';
  });
  const [sortDir, setSortDir] = useState<SortDir>(() => {
    const parsed = parseInventorySort(loadInventoryTablePreferences().sort);
    return parsed?.direction ?? 'desc';
  });
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    saveInventoryTablePreferences({
      sort: encodeInventorySort(sortKey, sortDir),
      perPage,
    });
  }, [sortKey, sortDir, perPage]);

  const buildTypeParam = (filter: TypeFilter) => {
    if (filter === 'receipt') return 'receipt';
    if (filter === 'sale') return 'sale';
    if (filter === 'adjustments') return 'adjustment_in,adjustment_out';
    if (filter === 'starting_value') return 'starting_value';
    if (filter === 'oversell') return 'backorder_sale,cogs_true_up';
    if (filter === 'all')
      return 'receipt,sale,adjustment_in,adjustment_out,starting_value,backorder_sale,cogs_true_up';
    return undefined;
  };

  const sortedMovements = useMemo(
    () => [...movements].sort((a, b) => compareMovements(a, b, sortKey, sortDir)),
    [movements, sortKey, sortDir]
  );

  const filteredColumnTotals = useMemo(
    () => computeInventoryMovementTotals(sortedMovements),
    [sortedMovements]
  );

  const visibleRowKeys = useMemo(
    () => sortedMovements.map(inventoryMovementRowKey),
    [sortedMovements]
  );

  const selectedRows = useMemo(
    () => sortedMovements.filter((row) => selectedKeys.has(inventoryMovementRowKey(row))),
    [sortedMovements, selectedKeys]
  );

  const selectedTotals = useMemo(
    () => computeInventoryMovementTotals(selectedRows),
    [selectedRows]
  );

  const allPageSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedKeys.has(key));

  const somePageSelected = visibleRowKeys.some((key) => selectedKeys.has(key));

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleRowKeys) {
          next.add(key);
        }
      } else {
        for (const key of visibleRowKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const handleSort = (column: SortKey) => {
    if (sortKey === column) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(column);
      setSortDir(defaultSortDir(column));
    }
  };

  const fetchMovements = async (pageNum: number) => {
    const requestId = ++fetchRequestIdRef.current;

    try {
      setLoading(true);
      setError(null);
      const fetchDates = resolveFetchDates(datePreset, startDate, endDate);
      const result = await getInventoryTransactions({
        type: buildTypeParam(typeFilter),
        item_id: parseItemFilterId(itemFilter),
        start_date: fetchDates.start_date,
        end_date: fetchDates.end_date,
        search: searchQuery || undefined,
        recent_days: 30,
        include_running_balance: true,
        per_page: perPage,
        page: pageNum,
      });

      if (requestId !== fetchRequestIdRef.current) return;

      setMovements(result.movements);
      setSummary(result.summary);
      setMeta(result.meta);
      if (result.meta.current_page !== pageNum) {
        updateFilters({ page: result.meta.current_page });
      }
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return;

      if (err instanceof ApiValidationError) {
        const firstError = Object.values(err.errors).flat()[0];
        setError(firstError || err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch inventory transactions');
      }
      setMovements([]);
      setSummary(null);
      setMeta(null);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const state = location.state as { itemId?: number; typeFilter?: TypeFilter } | null;
    if (!state?.typeFilter && !state?.itemId) return;

    const patch: Partial<typeof filters> = { page: 1 };
    if (state.typeFilter) {
      patch.typeFilter = state.typeFilter;
    }
    if (state.itemId) {
      patch.itemFilter = String(state.itemId);
    }
    updateFilters(patch);
    window.history.replaceState({}, document.title);
  }, [location.state, updateFilters]);

  useEffect(() => {
    selectedItemOptionRef.current = selectedItemOption;
  }, [selectedItemOption]);

  useEffect(() => {
    const itemId = parseItemFilterId(itemFilter);
    if (!itemId) return;

    void (async () => {
      try {
        const item = await getItemById(itemId);
        itemLookupRef.current.set(itemId, item);
        const option = itemToOption(item);
        setSelectedItemOption(option);
        selectedItemOptionRef.current = option;
        setItemOptions((prev) =>
          prev.some((opt) => String(opt.value) === String(itemId)) ? prev : [option, ...prev]
        );
      } catch {
        // filter still applies; label may stay empty until search resolves it
      }
    })();
  }, [itemFilter]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setItemSearchLoading(true);
      try {
        const result = await getItems({
          search: itemSearchInput.trim() || undefined,
          item_type: 'INVENTORY',
          active: true,
          per_page: 20,
          page: 1,
        });
        for (const item of result.items) {
          itemLookupRef.current.set(item.id, item);
        }
        const options = result.items.map(itemToOption);
        const selected = selectedItemOptionRef.current;
        setItemOptions(
          selected && !options.some((opt) => String(opt.value) === String(selected.value))
            ? [selected, ...options]
            : options
        );
      } catch {
        // silently ignore — dropdown just shows empty
      } finally {
        setItemSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [itemSearchInput]);

  useEffect(() => {
    if (datePreset === 'custom' && (!startDate || !endDate)) {
      return;
    }
    fetchMovements(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, itemFilter, startDate, endDate, searchQuery, datePreset, perPage, page]);

  const fetchRef = useRef(fetchMovements);
  fetchRef.current = fetchMovements;
  const pageRef = useRef(page);
  pageRef.current = page;

  useEffect(() => {
    registerRefetch(() => {
      void fetchRef.current(pageRef.current);
    });
  }, [
    registerRefetch,
    typeFilter,
    itemFilter,
    startDate,
    endDate,
    searchQuery,
    datePreset,
    perPage,
    page,
  ]);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [
    typeFilter,
    itemFilter,
    datePreset,
    startDate,
    endDate,
    searchQuery,
    page,
    perPage,
  ]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleRowKeys);
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleRowKeys]);

  useEffect(() => {
    const input = selectAllCheckboxRef.current;
    if (input) {
      input.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    updateFilters({ searchQuery: searchInput.trim(), page: 1 });
  };

  const handleDatePresetChange = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      if (!startDate || !endDate) {
        const fallback = getDateRangeForPreset('today');
        updateFilters({
          datePreset: preset,
          startDate: fallback.startDate,
          endDate: fallback.endDate,
          page: 1,
        });
      } else {
        updateFilters({ datePreset: preset, page: 1 });
      }
      return;
    }

    const range = getDateRangeForPreset(preset);
    updateFilters({
      datePreset: preset,
      startDate: range.startDate,
      endDate: range.endDate,
      page: 1,
    });
  };

  const handleCustomDateRangeChange = (from: string, to: string) => {
    updateFilters({ startDate: from, endDate: to, page: 1 });
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSelectedItemOption(null);
    selectedItemOptionRef.current = null;
    clearFilters();
  };

  const handleItemFilterChange = async (value: number | string) => {
    const idStr = value != null && value !== '' ? String(value) : '';
    const id = parseItemFilterId(idStr);

    updateFilters({ itemFilter: idStr, page: 1 });

    if (!id) {
      setSelectedItemOption(null);
      selectedItemOptionRef.current = null;
      return;
    }

    const existingOption = itemOptions.find((opt) => String(opt.value) === idStr);
    if (existingOption) {
      setSelectedItemOption(existingOption);
      selectedItemOptionRef.current = existingOption;
    }

    let item = itemLookupRef.current.get(id);
    if (!item) {
      item = await getItemById(id).catch(() => undefined);
      if (item) {
        itemLookupRef.current.set(id, item);
      }
    }

    if (item) {
      const option = itemToOption(item);
      setSelectedItemOption(option);
      selectedItemOptionRef.current = option;
      setItemOptions((prev) =>
        prev.some((opt) => String(opt.value) === idStr) ? prev : [option, ...prev]
      );
    }
  };

  const handleViewBill = (row: InventoryTransaction) => {
    navigate(
      {
        pathname: `/inventory-transactions/view/bill/${row.reference_id}`,
        search: listSearch,
      },
      {
        state:
          row.transaction_id != null ? { transactionId: row.transaction_id } : undefined,
        preventScrollReset: true,
      }
    );
  };

  const handleViewInvoice = (row: InventoryTransaction) => {
    navigate(
      {
        pathname: `/inventory-transactions/view/invoice/${row.reference_id}`,
        search: listSearch,
      },
      {
        state:
          row.transaction_id != null ? { transactionId: row.transaction_id } : undefined,
        preventScrollReset: true,
      }
    );
  };

  const handleViewJournal = (row: InventoryTransaction) => {
    if (row.transaction_id == null) return;
    navigate('/accounts', {
      state: {
        activeTab: 'journal',
        referenceTransactionId: row.transaction_id,
      },
    });
  };

  const handleViewStartingValue = (row: InventoryTransaction) => {
    navigate(
      {
        pathname: `/inventory-transactions/view/starting-value/${row.item_id}`,
        search: listSearch,
      },
      {
        state:
          row.transaction_id != null ? { transactionId: row.transaction_id } : undefined,
        preventScrollReset: true,
      }
    );
  };

  const handleViewAdjustment = (row: InventoryTransaction) => {
    navigate(
      {
        pathname: `/inventory-transactions/view/adjustment/${row.reference_id}`,
        search: listSearch,
      },
      {
        state:
          row.transaction_id != null ? { transactionId: row.transaction_id } : undefined,
        preventScrollReset: true,
      }
    );
  };

  const ROW_ACTION_PRIORITY: InventoryTransactionAction[] = [
    'view_bill',
    'view_invoice',
    'view_adjustment',
    'view_starting_value',
    'view_journal',
  ];

  const getRowAction = (row: InventoryTransaction) =>
    ROW_ACTION_PRIORITY.find((action) => row.available_actions.includes(action)) ?? null;

  const openMovement = (row: InventoryTransaction) => {
    const action = getRowAction(row);
    if (!action) return;

    switch (action) {
      case 'view_bill':
        handleViewBill(row);
        break;
      case 'view_invoice':
        handleViewInvoice(row);
        break;
      case 'view_adjustment':
        handleViewAdjustment(row);
        break;
      case 'view_starting_value':
        handleViewStartingValue(row);
        break;
      case 'view_journal':
        handleViewJournal(row);
        break;
    }
  };

  const summaryCards = summary
    ? [
        {
          key: 'on_hand' as const,
          label: 'On hand',
          primary: formatCurrency(summary.on_hand.total_value),
          secondary: `${formatQuantity(summary.on_hand.total_qty)} qty · ${summary.on_hand.item_count} items`,
          icon: Boxes,
          accent: 'border-emerald-200 bg-emerald-50',
          text: 'text-emerald-900',
          onClick: () => navigate('/inventory'),
        },
        {
          key: 'received_recent' as const,
          label: `Received (${summary.received_recent.days ?? 30}d)`,
          primary: formatCurrency(summary.received_recent.total_value),
          secondary: `${summary.received_recent.count} movements · ${formatQuantity(summary.received_recent.total_qty)} qty`,
          icon: ArrowDownToLine,
          accent: 'border-blue-200 bg-blue-50',
          text: 'text-blue-900',
          onClick: () => {
            updateFilters({ typeFilter: 'receipt', page: 1 });
          },
        },
        {
          key: 'sold_recent' as const,
          label: `Sold (${summary.sold_recent.days ?? 30}d)`,
          primary: formatCurrency(summary.sold_recent.total_cogs),
          secondary: `${summary.sold_recent.count} movements · ${formatQuantity(summary.sold_recent.total_qty)} qty`,
          icon: ArrowUpFromLine,
          accent: 'border-violet-200 bg-violet-50',
          text: 'text-violet-900',
          onClick: () => {
            updateFilters({ typeFilter: 'sale', page: 1 });
          },
        },
        {
          key: 'low_stock' as const,
          label: 'Low stock',
          primary: String(summary.low_stock.count),
          secondary: summary.low_stock.count === 1 ? 'item below reorder' : 'items below reorder',
          icon: AlertTriangle,
          accent: 'border-orange-200 bg-orange-50',
          text: 'text-orange-900',
          onClick: () => navigate('/inventory'),
        },
      ]
    : [];

  const totalsBarMode = selectedTotals.count > 0 ? 'selected' : 'page';
  const totalsBarData =
    totalsBarMode === 'selected' ? selectedTotals : filteredColumnTotals;
  const totalsBarPageSuffix =
    meta && meta.total > filteredColumnTotals.count ? ' on this page' : '';
  const showTotalsBar = sortedMovements.length > 0;

  return (
    <div className="flex w-full max-w-full flex-1 flex-col min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
                  Inventory Transactions
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  FIFO receipts, sales, opening stock, and quantity adjustments
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fetchMovements(page)}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <Link
                  to="/inventory-adjustments"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Adjustments
                </Link>
                <Link
                  to="/inventory?tab=stock-valuation"
                  className="flex items-center justify-center px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm font-medium"
                >
                  <Package className="h-5 w-5 mr-2" />
                  Stock Value
                </Link>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex gap-2 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => fetchMovements(page)} className="underline shrink-0">
                Retry
              </button>
            </div>
          )}

          {summary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
              {summaryCards.map((card) => {
                const Icon = card.icon;
                return (
                  <button
                    key={card.key}
                    type="button"
                    onClick={card.onClick}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${card.accent} hover:shadow-sm`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium ${card.text}`}>{card.label}</span>
                      <Icon className={`h-4 w-4 ${card.text}`} />
                    </div>
                    <p className={`text-2xl font-semibold ${card.text}`}>{card.primary}</p>
                    <p className="text-xs text-gray-500 mt-1">{card.secondary}</p>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mb-4 flex flex-wrap gap-2">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  updateFilters({ typeFilter: tab.id, page: 1 });
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  typeFilter === tab.id
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <form onSubmit={handleSearch} className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      placeholder="SKU, item, bill/invoice/adjustment, memo..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-800"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700"
                  >
                    Search
                  </button>
                </div>
              </form>
              <DateRangeSelect
                preset={datePreset}
                startDate={startDate}
                endDate={endDate}
                onPresetChange={handleDatePresetChange}
                onCustomRangeChange={handleCustomDateRangeChange}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
                <SearchableSelect
                  options={itemOptions}
                  value={parseItemFilterId(itemFilter) ?? ''}
                  onChange={(value) => void handleItemFilterChange(value)}
                  placeholder="All items"
                  searchPlaceholder="Search by name or SKU..."
                  clearValue=""
                  onSearchChange={setItemSearchInput}
                  isLoading={itemSearchLoading}
                />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {hasActiveFilters && (
                <>
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {meta?.total ?? movements.length} results
                    {filteredColumnTotals.count > 0 && (
                      <>
                        {' · '}
                        Qty {formatQuantity(filteredColumnTotals.qtyChange, { minimumFractionDigits: 0 })}
                        {' · '}
                        Value {formatCurrency(filteredColumnTotals.valueChange)}
                      </>
                    )}
                  </span>
                  <button onClick={handleClearFilters} className="text-sm text-slate-700 underline">
                    Clear
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            {loading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
              </div>
            ) : (
              <table className="w-full min-w-[1200px] border-separate border-spacing-0">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-slate-800"
                        aria-label="Select all on this page"
                      />
                    </th>
                    <SortableTableHeader label="Date" column="txn_date" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Type" column="movement_type" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Reference" column="reference_number" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Counterparty" column="counterparty_name" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Item" column="item_name" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Qty" column="qty_change" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                    <SortableTableHeader label="Unit cost" column="unit_cost" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                    <SortableTableHeader label="Value" column="value_change" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                    <SortableTableHeader label="On hand" column="qty_on_hand_after" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {sortedMovements.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-6 py-12 text-center">
                        <FileText className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">No inventory movements found.</p>
                      </td>
                    </tr>
                  ) : (
                    sortedMovements.map((row) => {
                      const qty = parseNum(row.qty_change);
                      const value = parseNum(row.value_change);
                      const rowAction = getRowAction(row);
                      const rowKey = inventoryMovementRowKey(row);
                      const isCheckboxSelected = selectedKeys.has(rowKey);
                      return (
                        <tr
                          key={rowKey}
                          className={`hover:bg-gray-50${
                            rowAction ? ' cursor-pointer' : ''
                          }${isCheckboxSelected ? ' bg-slate-50/60' : ''}`}
                          onClick={() => rowAction && openMovement(row)}
                        >
                          <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isCheckboxSelected}
                              onChange={(e) => toggleRow(rowKey, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-slate-800"
                              aria-label={`Select ${row.reference_number}`}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div>{formatDate(row.txn_date)}</div>
                            {row.created_at && (
                              <div className="text-xs text-gray-400 mt-0.5">
                                {formatTime(row.created_at)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${movementTypeBadgeClass(row.movement_type)}`}
                            >
                              {movementTypeLabel(row.movement_type)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-mono font-medium text-gray-900">
                              {row.reference_number}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatLabel(row.reference_type)}
                            </div>
                            {shouldShowRowMemo(row) && (
                              <div
                                className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]"
                                title={row.memo ?? undefined}
                              >
                                {row.memo}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {row.counterparty_name || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-medium text-gray-900">{row.item_name}</div>
                            <div className="text-xs text-gray-500 font-mono">{row.item_sku}</div>
                          </td>
                          <td
                            className={`px-4 py-3 text-sm text-right font-medium font-mono ${
                              qty >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            <div>
                              {qty >= 0 ? '+' : ''}
                              {formatQuantity(row.qty_change)}
                            </div>
                            {renderReceiptQtySplit(row)}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-mono">
                            {row.unit_cost != null ? formatCurrency(row.unit_cost) : '—'}
                          </td>
                          <td
                            className={`px-4 py-3 text-sm text-right font-medium font-mono ${
                              value >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {formatCurrency(row.value_change)}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-mono text-gray-700">
                            {row.qty_on_hand_after != null
                              ? formatQuantity(row.qty_on_hand_after)
                              : '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {meta && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-1 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <label htmlFor="inventory-transactions-per-page" className="text-gray-700">
                  Rows per page
                </label>
                <select
                  id="inventory-transactions-per-page"
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    updateFilters({ page: 1 });
                  }}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg bg-white text-sm"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <span className="text-gray-500">
                Page {meta.current_page} of {meta.last_page} · {meta.total} total
              </span>
              {meta.last_page > 1 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => updateFilters({ page: meta.current_page - 1 })}
                    disabled={meta.current_page <= 1 || loading}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => updateFilters({ page: meta.current_page + 1 })}
                    disabled={meta.current_page >= meta.last_page || loading}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
      </div>

      {showTotalsBar && !loading && (
        <InventoryMovementTotalsBar
          mode={totalsBarMode}
          count={totalsBarData.count}
          qtyChange={totalsBarData.qtyChange}
          valueChange={totalsBarData.valueChange}
          qtyAddedToStock={totalsBarData.qtyAddedToStock}
          hasReceiptBackorderSplit={totalsBarData.hasReceiptBackorderSplit}
          pageCountSuffix={totalsBarPageSuffix}
          onClear={totalsBarMode === 'selected' ? clearSelection : undefined}
        />
      )}
    </div>
  );
};

export default InventoryTransactions;
